import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Title, Button, Modal, Stack, TextInput, Text, Group, Loader, Grid, Textarea, Badge, Select, Checkbox, Anchor, Flex } from '@mantine/core';
import { IconSend, IconRobot, IconSearch, IconMapPin, IconPhone, IconWorld, IconMail } from '@tabler/icons-react';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import CompanyProfile from './CompanyProfile';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Component to dynamically change map center and force Leaflet tile size invalidation
function ChangeView({ center, companies, userLocation }) {
  const map = useMap();

  useEffect(() => {
    const invalidate = () => {
      try {
        if (map && typeof map.invalidateSize === 'function') {
          map.invalidateSize(true);
        }
      } catch (e) {
        console.warn("Map invalidateSize warning:", e);
      }
    };

    invalidate();
    const t1 = setTimeout(invalidate, 100);
    const t2 = setTimeout(invalidate, 350);
    const t3 = setTimeout(invalidate, 800);

    let observer;
    if (map && map._container && window.ResizeObserver) {
      observer = new ResizeObserver(() => invalidate());
      observer.observe(map._container);
    }
    window.addEventListener('resize', invalidate);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (observer && map._container) observer.unobserve(map._container);
      window.removeEventListener('resize', invalidate);
    };
  }, [map]);

  useEffect(() => {
    if (companies && companies.length > 0) {
      const bounds = L.latLngBounds(companies.map(c => [c.lat, c.lng]));
      if (userLocation) {
        bounds.extend(userLocation);
      }
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else if (center) {
      map.setView(center, map.getZoom());
    }
  }, [companies, userLocation, center[0], center[1], map]);
  return null;
}

export default function PublicMap({ externalQuery, onAddToCart }) {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatUserEmail, setChatUserEmail] = useState('');
  const [emailProvided, setEmailProvided] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [inputMsg, setInputMsg] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState('SERVICES');
  
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  
  const messagesEndRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const pendingUserMessagesRef = useRef([]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (messages.length > 0 && messages[messages.length - 1].sender === 'AI') {
      setLoadingAI(false);
    }
  }, [messages, loadingAI]);

  useEffect(() => {
    // Get user location on mount and fetch nearby companies
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            setUserLocation([lat, lng]);
            fetchResults('', lat, lng, 'SERVICES');
        },
        (error) => {
            console.warn("Geolocation denied or failed", error);
            fetchResults('', null, null, 'SERVICES');
        },
        { timeout: 5000 }
      );
    } else {
        fetchResults('', null, null, 'SERVICES');
    }
  }, []);

  useEffect(() => {
    let interval;
    if (chatOpen && chatSessionId) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/chat/history/${chatSessionId}`);
          if (res.ok) {
            const data = await res.json();
            if (data && data.length > 0) {
              const formatted = data.map(m => ({
                sender: m.role === 'USER' ? 'user' : (m.role === 'OWNER' ? 'OWNER' : 'AI'),
                text: m.content,
                id: m.id
              }));
              setMessages(prev => {
                const hasGreeting = prev.length > 0 && prev[0].sender === 'AI' && !prev[0].id;
                return hasGreeting ? [prev[0], ...formatted] : formatted;
              });
            }
          }
        } catch (e) {
          console.error("Failed to poll chat:", e);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [chatOpen, chatSessionId, selectedCompany, t]);

  const handleSearch = (queryOverride = null) => {
    const q = queryOverride !== null ? queryOverride : searchQuery;
    if (!q || !q.trim()) {
      setCompanies([]);
      return;
    }

    setSearching(true);
    
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setUserLocation([lat, lng]);
          fetchResults(q, lat, lng, searchMode);
        },
        (error) => {
          console.warn("Geolocation denied or failed", error);
          fetchResults(q, null, null, searchMode);
        },
        { timeout: 5000 }
      );
    } else {
      fetchResults(q, null, null, searchMode);
    }
  };

  const fetchResults = (q, lat, lng, mode) => {
    fetch(`${API_BASE_URL}/api/public/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: q, lat, lng, mode })
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCompanies(data);
        } else {
          setCompanies([]);
        }
        setSearching(false);
      })
      .catch(err => {
        console.error("Error fetching companies:", err);
        setCompanies([]);
        setSearching(false);
      });
  };

  useEffect(() => {
    if (externalQuery !== undefined && externalQuery !== null) {
      setSearchQuery(externalQuery);
      if (externalQuery.trim() !== '') {
        handleSearch(externalQuery);
      } else {
        fetchResults('', userLocation ? userLocation[0] : null, userLocation ? userLocation[1] : null, searchMode);
      }
    }
  }, [externalQuery]);

  const openChat = (comp) => {
    setSelectedCompany(comp);
    setChatOpen(true);
    setChatSessionId('session-' + Math.random().toString(36).substr(2, 9));
    setMessages([{ sender: 'AI', text: t('greeting', { name: comp.name }) }]);
  };

  const openDetails = async (comp) => {
    setLoadingDetails(true);
    setDetailsOpen(true);
    try {
        const res = await fetch(`${API_BASE_URL}/api/public/company/${comp.id}`);
        if (res.ok) {
            const data = await res.json();
            setSelectedCompanyDetails({...comp, ...data});
        } else {
            setSelectedCompanyDetails(comp);
        }
    } catch (e) {
        setSelectedCompanyDetails(comp);
    }
    setLoadingDetails(false);
  };

  const sendMessage = () => {
    if (!inputMsg.trim()) return;
    const newMsg = inputMsg;
    setInputMsg('');
    setLoadingAI(true);

    setMessages(prev => [...prev, { sender: 'user', text: newMsg }]);
    pendingUserMessagesRef.current.push(newMsg);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      const combinedMessage = pendingUserMessagesRef.current.join('\n');
      pendingUserMessagesRef.current = [];

      try {
          const res = await fetch(`${API_BASE_URL}/api/chat/send`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  ownerEmail: selectedCompany.email,
                  content: combinedMessage,
                  sessionId: chatSessionId,
                  source: "PUBLIC",
                  visitorEmail: chatUserEmail
              })
          });
          const data = await res.json();
          if (data.role !== 'SYSTEM' && data.role !== undefined) {
              const responseText = data.content || data.text || data.response || (typeof data === 'string' ? data : JSON.stringify(data));
              setMessages(prev => [...prev, { sender: 'AI', text: responseText }]);
          }
      } catch (e) {
          setMessages(prev => [...prev, { sender: 'AI', text: "Error connecting to AI." }]);
      }
    }, 2000);
  };

  const mapCenter = userLocation || (companies.length > 0 ? [companies[0].lat, companies[0].lng] : [52.0693, 19.4803]);

  return (
    <>
    <Flex 
      direction={{ base: 'column', md: 'row' }} 
      style={{ width: '100%', minHeight: 'calc(100vh - 80px)', overflow: 'hidden' }}
    >
        <Box 
          px="md" py="md" 
          w={{ base: '100%', md: '70%' }} 
          style={{ height: 'calc(100vh - 80px)', overflowY: 'auto' }}
        >
          <Title order={3} mb="md" c="indigo.9">
            {searchQuery ? `${t('search_results_title', 'Wyniki wyszukiwania na mapie dla')}: "${searchQuery}" (${companies.length})` : `${t('all_companies_list_title', 'Katalog Zweryfikowanych Firm i Mapa Interaktywna AILinkB')} (${companies.length})`}
          </Title>
          <Stack>
            {companies.length === 0 ? (
              <Text c="dimmed" ta="center">{t('no_companies_found')}</Text>
            ) : (
              companies.map(comp => (
                <Box key={comp.id} p="md" style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', border: '1px solid #e2e8f0' }}>
                  <Text fw={700} size="lg" c="indigo.9">{comp.name}</Text>
                  {comp.address && <Text size="xs" c="dimmed" mt="4px" mb="4px" style={{ wordBreak: 'break-word' }}><strong>{t('address')}</strong> {comp.address}</Text>}
                  
                  {comp.serviceNames && comp.serviceNames.length > 0 && (
                      <Group gap="xs" mt="4px" mb="8px">
                          {comp.serviceNames.slice(0, 3).map((name, idx) => (
                              <Badge key={idx} size="sm" variant="light" color="indigo">{name}</Badge>
                          ))}
                          {comp.serviceNames.length > 3 && (
                              <Badge size="sm" variant="dot" color="gray">+{comp.serviceNames.length - 3} {t('more')}</Badge>
                          )}
                      </Group>
                  )}

                  {comp.description && <Text size="sm" c="gray.7" mt="4px" mb="8px" lineClamp={2} style={{ fontStyle: 'italic' }}>{comp.description}</Text>}
                  {comp.distance != null && comp.distance < 99999 && <Text size="sm" c="dimmed" mb="sm"><IconMapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }}/>{Math.round(comp.distance)} {t('km_away')}</Text>}
                  <Group gap="sm">
                    <Button component="a" href={`/?profile=${window.btoa(comp.email)}`} target="_blank" flex={1} size="sm" color="gray" radius="md" variant="light">
                      {t('view_details')}
                    </Button>
                  </Group>
                </Box>
              ))
            )}
          </Stack>
        </Box>

        <Box 
          px="md" py="md" 
          w={{ base: '100%', md: '30%' }} 
          style={{ height: 'calc(100vh - 80px)', position: 'relative', zIndex: 1 }}
        >
          <Box style={{ height: '100%', width: '100%', display: 'flex', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', position: 'relative', zIndex: 1 }}>
          <MapContainer center={mapCenter} zoom={userLocation ? 10 : 6} style={{ height: '100%', width: '100%', zIndex: 1 }}>
            <ChangeView center={mapCenter} companies={companies} userLocation={userLocation} />
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; OpenStreetMap contributors &copy; CARTO'
            />

            {companies.map(comp => (
              <Marker key={comp.id} position={[comp.lat, comp.lng]}>
                <Popup>
                  <div style={{ textAlign: 'center', minWidth: '150px' }}>
                    <strong style={{ fontSize: '1.1rem', color: '#1e1b4b' }}>{comp.name}</strong><br/>
                    {comp.address && <Text size="xs" c="dimmed" mt="4px" style={{ wordBreak: 'break-word' }}>{comp.address}</Text>}
                    {comp.distance != null && comp.distance < 99999 && <Text size="xs" c="dimmed" mt="4px">{Math.round(comp.distance)} {t('km_away')}</Text>}
                    <Group mt="sm" justify="center" gap="xs">
                      <Button component="a" href={`/?profile=${window.btoa(comp.email)}`} target="_blank" size="xs" color="gray" variant="light" radius="xl">
                        {t('view_details')}
                      </Button>
                    </Group>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          </Box>
        </Box>
    </Flex>

      <Modal opened={chatOpen} onClose={() => setChatOpen(false)} title={`${selectedCompany?.name} AI Assistant`} radius="md" size="xl">
        {!emailProvided ? (
          <Stack gap="md" align="center" py="xl">
            <Text>{t('Please enter your email to start the chat:')}</Text>
            <TextInput 
              placeholder="your@email.com" 
              value={chatUserEmail} 
              onChange={e => setChatUserEmail(e.target.value)} 
              w="100%"
              onKeyDown={e => { if (e.key === 'Enter' && chatUserEmail && consentGiven) setEmailProvided(true); }}
            />
            <Checkbox
              label={
                <Text size="sm">
                  {t('chat_consent_label')}
                  <Anchor onClick={() => setPrivacyOpen(true)} size="sm">
                    {t('privacy_policy_link')}
                  </Anchor>
                </Text>
              }
              checked={consentGiven}
              onChange={(event) => setConsentGiven(event.currentTarget.checked)}
              mt="md"
            />
            <Button color="indigo" disabled={!consentGiven || !chatUserEmail} onClick={() => { if (chatUserEmail && consentGiven) setEmailProvided(true); }}>{t('Start Chat')}</Button>
          </Stack>
        ) : (
        <Stack gap="md">
          <Box style={{ height: '60vh', minHeight: '300px', maxHeight: '500px', overflowY: 'auto', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start', background: m.sender === 'user' ? '#4f46e5' : '#ffffff', color: m.sender === 'user' ? 'white' : '#334155', padding: '10px 14px', borderRadius: '12px', borderBottomRightRadius: m.sender === 'user' ? '4px' : '12px', borderBottomLeftRadius: m.sender === 'AI' ? '4px' : '12px', marginBottom: '8px', maxWidth: '85%', border: m.sender === 'AI' ? '1px solid #e2e8f0' : 'none' }}>
                {m.text}
              </div>
            ))}
            {loadingAI && (
              <div style={{ alignSelf: 'flex-start', background: '#ffffff', padding: '12px 16px', borderRadius: '12px', borderBottomLeftRadius: '4px', marginBottom: '8px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Loader color="indigo" size="xs" variant="dots" />
                <Text size="sm" c="dimmed" fs="italic">{t('typing')}</Text>
              </div>
            )}
            <div ref={messagesEndRef} />
          </Box>
          <div style={{ display: 'flex', gap: '10px' }}>
            <TextInput style={{ flex: 1 }} placeholder={t('type_message')} value={inputMsg} onChange={e => setInputMsg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendMessage(); } }} />
            <Button color="indigo" onClick={sendMessage} radius="xl"><IconSend size={18}/></Button>
          </div>
        </Stack>
        )}
      </Modal>

      <Modal opened={detailsOpen} onClose={() => setDetailsOpen(false)} title={t('company_details')} radius="md" size="xl">
        {loadingDetails ? (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Loader color="indigo" />
            </Box>
        ) : selectedCompanyDetails ? (
            <Box style={{ 
                width: '100%', 
                maxWidth: '400px', 
                margin: '0 auto', 
                border: '12px solid #1e293b', 
                borderRadius: '36px', 
                overflow: 'hidden',
                height: '75vh',
                maxHeight: '800px',
                position: 'relative',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                background: '#f8fafc'
            }}>
                {/* iPhone Notch */}
                <Box style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '120px',
                    height: '24px',
                    background: '#1e293b',
                    borderBottomLeftRadius: '16px',
                    borderBottomRightRadius: '16px',
                    zIndex: 1000
                }} />
                {/* Content wrapper */}
                <Box style={{ height: '100%', width: '100%', overflowY: 'auto' }}>
                    <CompanyProfile email={selectedCompanyDetails.email} onAddToCart={onAddToCart} />
                </Box>
            </Box>
        ) : (
            <Text>{t('no_details')}</Text>
        )}
      </Modal>

      <Modal opened={privacyOpen} onClose={() => setPrivacyOpen(false)} title={t('privacy_policy_title')} radius="md" size="lg">
        <Text size="sm" style={{ whiteSpace: 'pre-line' }}>
          {t('privacy_policy_content')}
        </Text>
      </Modal>

    </>
  );
}
